import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DashboardStateService } from './dashboard-state.service';
import { AuthService } from './auth.service';
import { DashboardData, ApiResponse, getCurrentShiftFromLocalTime } from '@app/models/dashboard.models';
import { ShiftProductionRate, normalizeShiftProductionRate } from '@app/models/machine-production-rate.model';
import { environment } from '@environments/environment';
import { switchMap, catchError, of, forkJoin, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DashboardApiService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly stateService = inject(DashboardStateService);
  private readonly authService = inject(AuthService);
  private readonly baseUrl = `${environment.proxyUrls.productionApi}/api/v1`;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  startPolling(): void {
    this.fetchData();
    this.pollIntervalId = setInterval(() => {
      this.fetchData();
    }, environment.dashboard.fetchInterval);
  }

  private fetchData(): void {
    this.authService.ensureAuthenticated().pipe(
      switchMap((authenticated) => {
        if (!authenticated) {
          console.error('Authentication failed, skipping fetch');
          return of(null);
        }
        return this.fetchAll().pipe(
          catchError((err: HttpErrorResponse) => {
            if (err.status === 401) {
              localStorage.removeItem('SierraToken');
              return this.authService.login().pipe(
                switchMap((reauthed) => (reauthed ? this.fetchAll() : of(null))),
              );
            }
            console.error('Dashboard fetch error:', err);
            return of(null);
          }),
        );
      }),
    ).subscribe({
      next: (combined) => {
        if (combined?.dashboard?.success && combined.dashboard.data) {
          const productionRates = combined.production?.success && combined.production.data
            ? normalizeShiftProductionRate(combined.production.data as never)
            : combined.dashboard.data.machineProductionRates;
          const shift = getCurrentShiftFromLocalTime();
          const merged: DashboardData = {
            ...combined.dashboard.data,
            shift,
            machineProductionRates: productionRates,
          };
          this.stateService.setDashboardData(merged, shift);
        }
      },
    });
  }

  private fetchAll(): Observable<{
    dashboard: ApiResponse<DashboardData>;
    production: ApiResponse<ShiftProductionRate> | null;
  }> {
    return forkJoin({
      dashboard: this.http.get<ApiResponse<DashboardData>>(
        `${this.baseUrl}/knitting-dashboard/status`,
      ),
      production: this.http
        .get<ApiResponse<ShiftProductionRate>>(
          `${this.baseUrl}/knitting-production/lbs-per-hour-by-machine`,
        )
        .pipe(
          catchError((err: HttpErrorResponse) => {
            if (err.status === 401) throw err;
            console.error('Lbs-per-hour fetch error:', err);
            return of(null);
          }),
        ),
    });
  }

  ngOnDestroy(): void {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
    }
  }
}
